import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { MediaPicker, MediaUrlField } from "@/components/admin/MediaPicker";
import { usesLinkedProductImage } from "@/lib/packs/item-media";
import { adminPackQuery, packEligibleProductsQuery } from "@/lib/packs/admin-queries";
import { savePackFn } from "@/lib/packs/packs.admin.functions";
import {
  PACK_BOTTLE_SIZES,
  PACK_STATUSES,
  PACK_TYPES,
  emptyPackInput,
  slugifyPackName,
  validatePackInput,
  type PackInput,
  type PackOverrideInput,
} from "@/lib/packs/admin-types";
import { SALES_CHANNELS, salesChannelLabel } from "@/lib/catalog/sales-channels";
import {
  PACK_ADDON_TYPES,
  PACK_CATEGORIES,
  PACK_COMPOSITION_MODES,
  PACK_FIXED_ITEM_TYPES,
  PACK_OCCASIONS,
  PACK_SORTS,
  type PackOccasion,
} from "@/lib/packs/types";
import { formatPrice } from "@/lib/cart/pricing";

export const Route = createFileRoute("/admin/packs/$id")({ component: PackEditorPage, head: () => ({ meta: [
  { title: "Edit Gift Pack | CB16 Admin" },
  { name: "description", content: "Manage CB16 gift pack contents and imagery." },
  { property: "og:title", content: "Edit Gift Pack | CB16 Admin" },
  { property: "og:description", content: "Manage CB16 gift pack contents and imagery." },
  { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" },
] }) });

function PackEditorPage() {
  const { id } = useParams({ from: "/admin/packs/$id" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isNew = id === "new";
  const { data: pack, isLoading } = useQuery(adminPackQuery(id));

  const [form, setForm] = useState<PackInput>(() => emptyPackInput());
  const [loaded, setLoaded] = useState(isNew);
  const [picker, setPicker] = useState<null | "heroImage" | "bannerImage" | "thumbnailImage">(null);
  const [fixedImageSlot, setFixedImageSlot] = useState<number | null>(null);

  useEffect(() => {
    if (isNew || !pack || loaded) return;
    setForm({
      id: pack.id,
      slug: pack.slug,
      name: pack.name,
      status: pack.status,
      salesChannel: pack.salesChannel,
      packType: pack.packType,
      packCategory: pack.packCategory,
      compositionMode: pack.compositionMode,
      occasion: pack.occasion,
      giftOptions: pack.giftOptions,
      bottleSizeMl: pack.bottleSizeMl,
      minBottles: pack.minBottles,
      maxBottles: pack.maxBottles,
      shortDescription: pack.shortDescription ?? "",
      description: pack.description ?? "",
      marketingText: pack.marketingText ?? "",
      heroImage: pack.heroImage ?? "",
      bannerImage: pack.bannerImage ?? "",
      thumbnailImage: pack.thumbnailImage ?? "",
      pricingRules: pack.pricingRules,
      seo: { title: pack.seo.title ?? "", description: pack.seo.description ?? "", image: pack.seo.image ?? "" },
      defaultSort: pack.defaultSort,
      isFeatured: pack.isFeatured,
      sortOrder: pack.sortOrder,
      scheduledAt: pack.scheduledAt,
      internalNotes: pack.internalNotes ?? "",
      variants: pack.variants.map((v) => ({
        id: v.id, label: v.label, bottleCount: v.bottleCount, bottlesPerBox: v.bottlesPerBox,
        sortOrder: v.sortOrder, isActive: v.isActive,
      })),
      overrides: pack.overrides,
      fixedItems: pack.fixedItems.map((i) => ({
        id: i.id, variantId: i.variantId, productId: i.productId, itemType: i.itemType,
        label: i.label, qty: i.qty, sizeMl: i.sizeMl, image: i.image ?? "", sortOrder: i.sortOrder,
      })),
      addons: pack.addons.map((a) => ({
        id: a.id, addonType: a.addonType, label: a.label, description: a.description ?? "",
        image: a.image ?? "", price: a.price, isDefault: a.isDefault, isRequired: a.isRequired,
        maxQty: a.maxQty, isActive: a.isActive, sortOrder: a.sortOrder,
      })),
    });
    setLoaded(true);
  }, [pack, isNew, loaded]);

  const { data: eligible = [] } = useQuery(packEligibleProductsQuery(form.salesChannel, form.bottleSizeMl));
  const errors = useMemo(() => validatePackInput(form), [form]);

  const saveMut = useMutation({
    mutationFn: (input: PackInput) => savePackFn({ data: input }),
    onSuccess: (res) => {
      toast.success("Pack saved");
      void qc.invalidateQueries({ queryKey: ["admin", "packs"] });
      if (isNew) void navigate({ to: "/admin/packs/$id", params: { id: res.id } });
      else setForm((f) => ({ ...f, id: res.id }));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function set<K extends keyof PackInput>(key: K, value: PackInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function overrideFor(productId: string): PackOverrideInput {
    return form.overrides.find((o) => o.productId === productId) ?? { productId, sortOrder: 0, isPinned: false, isExcluded: false };
  }

  function patchFixedItem(index: number, patch: Partial<PackInput["fixedItems"][number]>) {
    setForm((f) => ({ ...f, fixedItems: f.fixedItems.map((i, x) => (x === index ? { ...i, ...patch } : i)) }));
  }
  function patchAddon(index: number, patch: Partial<PackInput["addons"][number]>) {
    setForm((f) => ({ ...f, addons: f.addons.map((a, x) => (x === index ? { ...a, ...patch } : a)) }));
  }

  function patchOverride(productId: string, patch: Partial<PackOverrideInput>) {
    setForm((f) => {
      const next = f.overrides.filter((o) => o.productId !== productId);
      const merged = { ...overrideFor(productId), ...patch };
      if (merged.isPinned || merged.isExcluded || merged.sortOrder !== 0) next.push(merged);
      return { ...f, overrides: next };
    });
  }

  function save(status?: PackInput["status"]) {
    const payload = status ? { ...form, status } : form;
    const issues = validatePackInput(payload);
    if (issues.length > 0) { toast.error(issues[0] ?? "Invalid pack"); return; }
    if (status) set("status", status);
    saveMut.mutate(payload);
  }

  if (!isNew && isLoading) return <div className="p-6 text-black/60">Loading pack…</div>;
  if (!isNew && !isLoading && !pack) return <div className="p-6 text-black/60">Pack not found.</div>;

  const included = eligible.filter((p) => !overrideFor(p.id).isExcluded);
  const cheapest = included.length > 0 ? Math.min(...included.map((p) => p.unitPrice)) : 0;
  const dearest = included.length > 0 ? Math.max(...included.map((p) => p.unitPrice)) : 0;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={isNew ? "New pack" : form.name || "Edit pack"}
        subtitle={`${form.bottleSizeMl} ML bottles · ${salesChannelLabel(form.salesChannel)} channel · admin only, not customer facing`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={form.status === "published" ? "success" : form.status === "scheduled" ? "info" : "warn"}>{form.status}</StatusPill>
            <button type="button" className="border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-black/5" onClick={() => void navigate({ to: "/admin/packs" })}>Cancel</button>
            <button type="button" disabled={saveMut.isPending} className="border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-black/5 disabled:opacity-50" onClick={() => save("draft")}>Save draft</button>
            <button type="button" disabled={saveMut.isPending} className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-black/85 disabled:opacity-50" onClick={() => save()}>{saveMut.isPending ? "Saving…" : "Save"}</button>
            <button type="button" disabled={saveMut.isPending} className="border border-[#C6A15B] bg-[#C6A15B] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-black hover:bg-[#b8934f] disabled:opacity-50" onClick={() => save("published")}>Publish</button>
          </div>
        }
      />

      {errors.length > 0 && (
        <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">Fix before publishing</p>
          <ul className="mt-1 list-disc pl-5">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-5">
          <Card title="Pack details">
            <Grid>
              <Field label="Name">
                <input className="inp" value={form.name} onChange={(e) => {
                  const name = e.target.value;
                  setForm((f) => ({ ...f, name, slug: !f.id && (!f.slug || f.slug === slugifyPackName(f.name)) ? slugifyPackName(name) : f.slug }));
                }} />
              </Field>
              <Field label="Slug"><input className="inp" value={form.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
              <Field label="Sales channel">
                <select className="inp" value={form.salesChannel} onChange={(e) => set("salesChannel", e.target.value)}>
                  {SALES_CHANNELS.filter((c) => c.id !== "individual").map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Bottle size (ML)">
                <select className="inp" value={form.bottleSizeMl} onChange={(e) => set("bottleSizeMl", Number(e.target.value))}>
                  {PACK_BOTTLE_SIZES.map((s) => <option key={s} value={s}>{s} ML</option>)}
                </select>
              </Field>
              <Field label="Minimum bottles"><input type="number" min={1} className="inp" value={form.minBottles} onChange={(e) => set("minBottles", Number(e.target.value))} /></Field>
              <Field label="Maximum bottles"><input type="number" min={1} className="inp" value={form.maxBottles} onChange={(e) => set("maxBottles", Number(e.target.value))} /></Field>
              <Field label="Status">
                <select className="inp" value={form.status} onChange={(e) => set("status", e.target.value as PackInput["status"])}>
                  {PACK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Publish at (scheduled)">
                <input type="datetime-local" className="inp" value={form.scheduledAt ? form.scheduledAt.slice(0, 16) : ""}
                  onChange={(e) => set("scheduledAt", e.target.value ? new Date(e.target.value).toISOString() : null)} />
              </Field>
              <Field label="Display order"><input type="number" className="inp" value={form.sortOrder} onChange={(e) => set("sortOrder", Number(e.target.value))} /></Field>
              <Field label="Internal notes (staff only)" full>
                <textarea
                  className="inp min-h-20"
                  placeholder="Optional. Visible to staff only — never shown to customers."
                  value={form.internalNotes}
                  onChange={(e) => set("internalNotes", e.target.value)}
                />
              </Field>
              <Field label="Default product sort">
                <select className="inp" value={form.defaultSort} onChange={(e) => set("defaultSort", e.target.value as PackInput["defaultSort"])}>
                  {PACK_SORTS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Featured">
                <label className="flex items-center gap-2 pt-2 text-sm">
                  <input type="checkbox" checked={form.isFeatured} onChange={(e) => set("isFeatured", e.target.checked)} /> Highlight this pack
                </label>
              </Field>
            </Grid>
          </Card>

          <Card title="Pack category & composition" subtitle="Discovery packs stay on customer choice. Gift packs can be fixed or hybrid.">
            <Grid>
              <Field label="Pack category">
                <select className="inp" value={form.packCategory} onChange={(e) => set("packCategory", e.target.value as PackInput["packCategory"])}>
                  {PACK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Composition mode">
                <select className="inp" value={form.compositionMode} onChange={(e) => set("compositionMode", e.target.value as PackInput["compositionMode"])}>
                  {PACK_COMPOSITION_MODES.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Occasion">
                <select className="inp" value={form.occasion ?? ""} onChange={(e) => set("occasion", (e.target.value || null) as PackOccasion | null)}>
                  <option value="">None</option>
                  {PACK_OCCASIONS.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Gift message max length">
                <input type="number" min={0} max={1000} className="inp" value={form.giftOptions.messageMaxLength}
                  onChange={(e) => set("giftOptions", { ...form.giftOptions, messageMaxLength: Number(e.target.value) })} />
              </Field>
              <Field label="Gift experience" full>
                <div className="flex flex-wrap gap-4 pt-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.giftOptions.allowGiftMessage}
                      onChange={(e) => set("giftOptions", { ...form.giftOptions, allowGiftMessage: e.target.checked })} /> Gift message
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.giftOptions.allowRecipientName}
                      onChange={(e) => set("giftOptions", { ...form.giftOptions, allowRecipientName: e.target.checked })} /> Recipient name
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.giftOptions.allowHidePrices}
                      onChange={(e) => set("giftOptions", { ...form.giftOptions, allowHidePrices: e.target.checked })} /> Hide prices option
                  </label>
                </div>
              </Field>
            </Grid>
          </Card>

          {form.compositionMode !== "customer_choice" && (
            <Card title="Fixed contents" subtitle="Curated items always included in this pack. Hybrid packs also let customers pick the remaining bottles.">
              <div className="flex flex-col gap-3">
                {form.fixedItems.map((item, i) => (
                  <div key={item.id ?? `fx-${i}`} className="grid gap-3 border border-black/10 p-3 sm:grid-cols-[150px_1fr_150px_90px_90px_auto]">
                    <Field label="Type">
                      <select className="inp" value={item.itemType} onChange={(e) => patchFixedItem(i, { itemType: e.target.value as never })}>
                        {PACK_FIXED_ITEM_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                      </select>
                    </Field>
                    <Field label="Label"><input className="inp" value={item.label} onChange={(e) => patchFixedItem(i, { label: e.target.value })} /></Field>
                    <Field label="Linked product">
                      <select className="inp" value={item.productId ?? ""} onChange={(e) => {
                        const productId = e.target.value || null;
                        const product = eligible.find((p) => p.id === productId);
                        patchFixedItem(i, { productId, label: item.label || product?.name || "" });
                      }}>
                        <option value="">None</option>
                        {eligible.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </Field>
                    <Field label="Qty"><input type="number" min={1} className="inp" value={item.qty} onChange={(e) => patchFixedItem(i, { qty: Number(e.target.value) })} /></Field>
                    <Field label="Size ML"><input type="number" min={0} className="inp" value={item.sizeMl ?? ""} onChange={(e) => patchFixedItem(i, { sizeMl: e.target.value ? Number(e.target.value) : null })} /></Field>
                    <div className="flex items-end pb-1">
                      <button type="button" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-red-700 hover:underline"
                        onClick={() => set("fixedItems", form.fixedItems.filter((_, x) => x !== i))}>Remove</button>
                    </div>
                    {!usesLinkedProductImage(item.itemType) && (
                      <div className="sm:col-span-6">
                        {form.slug === "diwali-gift-collection" && item.id && <Field label="Included item price (₹)">
                          <input type="number" min={0} step="0.01" className="inp" value={Number((form.pricingRules["fixedItemPrices"] as Record<string, number> | undefined)?.[item.id] ?? 0)}
                            onChange={(e) => { const itemId = item.id; if (!itemId) return;
                              set("pricingRules", { ...form.pricingRules, fixedItemPrices: {
                                ...((form.pricingRules["fixedItemPrices"] as Record<string, number> | undefined) ?? {}), [itemId]: Number(e.target.value),
                              } }); }} />
                        </Field>}
                        <Field label="Image">
                          <div className="flex items-center gap-2">
                            <input className="inp flex-1" placeholder="Image URL" value={item.image ?? ""}
                              onChange={(e) => patchFixedItem(i, { image: e.target.value })} />
                            <button type="button" className="border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-black/5"
                              onClick={() => setFixedImageSlot(i)}>Library</button>
                            {item.image && <img src={item.image} alt="" className="h-10 w-10 border border-black/10 object-cover" />}
                          </div>
                        </Field>
                      </div>
                    )}
                  </div>
                ))}
                <button type="button" className="self-start border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-black/5"
                  onClick={() => set("fixedItems", [...form.fixedItems, {
                    id: crypto.randomUUID(), variantId: null, productId: null, itemType: "bottle" as const, label: "", qty: 1,
                    sizeMl: form.bottleSizeMl, image: "", sortOrder: form.fixedItems.length,
                  }])}>
                  Add fixed item
                </button>
                <MediaPicker
                  open={fixedImageSlot !== null}
                  onClose={() => setFixedImageSlot(null)}
                  onSelect={(a) => { if (fixedImageSlot !== null) patchFixedItem(fixedImageSlot, { image: a.url }); setFixedImageSlot(null); }}
                />
              </div>
            </Card>
          )}

          {form.packCategory !== "discovery" && (
            <Card title="Add-ons" subtitle="Optional paid extras: gift wrapping, greeting cards, premium boxes, free gifts.">
              <div className="flex flex-col gap-3">
                {form.addons.map((addon, i) => (
                  <div key={addon.id ?? `ad-${i}`} className="grid gap-3 border border-black/10 p-3 sm:grid-cols-[150px_1fr_110px_90px_auto]">
                    <Field label="Type">
                      <select className="inp" value={addon.addonType} onChange={(e) => patchAddon(i, { addonType: e.target.value as never })}>
                        {PACK_ADDON_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                      </select>
                    </Field>
                    <Field label="Label"><input className="inp" value={addon.label} onChange={(e) => patchAddon(i, { label: e.target.value })} /></Field>
                    <Field label="Price (₹)"><input type="number" min={0} className="inp" value={addon.price} onChange={(e) => patchAddon(i, { price: Number(e.target.value) })} /></Field>
                    <Field label="Max qty"><input type="number" min={1} className="inp" value={addon.maxQty} onChange={(e) => patchAddon(i, { maxQty: Number(e.target.value) })} /></Field>
                    <div className="flex flex-wrap items-end gap-3 pb-1 text-sm">
                      <label className="flex items-center gap-2"><input type="checkbox" checked={addon.isDefault} onChange={(e) => patchAddon(i, { isDefault: e.target.checked })} /> Default</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={addon.isRequired} onChange={(e) => patchAddon(i, { isRequired: e.target.checked })} /> Required</label>
                      <label className="flex items-center gap-2"><input type="checkbox" checked={addon.isActive} onChange={(e) => patchAddon(i, { isActive: e.target.checked })} /> Active</label>
                      <button type="button" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-red-700 hover:underline"
                        onClick={() => set("addons", form.addons.filter((_, x) => x !== i))}>Remove</button>
                    </div>
                    <Field label="Description" full><textarea className="inp min-h-14" value={addon.description} onChange={(e) => patchAddon(i, { description: e.target.value })} /></Field>
                    {!usesLinkedProductImage(addon.addonType) && <div className="sm:col-span-5">
                      <MediaUrlField label="Image" value={addon.image} onChange={(image) => patchAddon(i, { image })} />
                      {addon.image && <img src={addon.image} alt={addon.label} className="mt-2 h-10 w-10 object-cover" />}
                    </div>}
                  </div>
                ))}
                <button type="button" className="self-start border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-black/5"
                  onClick={() => set("addons", [...form.addons, {
                    addonType: "wrapping" as const, label: "", description: "", image: "", price: 0,
                    isDefault: false, isRequired: false, maxQty: 1, isActive: true, sortOrder: form.addons.length,
                  }])}>
                  Add add-on
                </button>
              </div>
            </Card>
          )}

          <Card title="Pricing">
            <Grid>
              <Field label="Pricing strategy">
                <select className="inp" value={form.packType} onChange={(e) => set("packType", e.target.value as PackInput["packType"])}>
                  {PACK_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              {(form.packType === "fixed_price" || form.packType === "fixed_plus_addons") && (
                <Field label="Base pack price (₹)">
                  <input type="number" min={0} className="inp" value={Number(form.pricingRules["fixedPrice"] ?? 0)}
                    onChange={(e) => set("pricingRules", { ...form.pricingRules, fixedPrice: Number(e.target.value) })} />
                </Field>
              )}
              {form.packType === "discounted" && (
                <Field label="Discount (%)">
                  <input type="number" min={0} max={99} className="inp" value={Number(form.pricingRules["discountPercent"] ?? 0)}
                    onChange={(e) => set("pricingRules", { ...form.pricingRules, discountPercent: Number(e.target.value) })} />
                </Field>
              )}
            </Grid>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.pricingRules["freeShipping"] === true}
                onChange={(e) => set("pricingRules", { ...form.pricingRules, freeShipping: e.target.checked })} />
              Free Standard Delivery for orders containing this pack (overrides the store threshold for this pack only)
            </label>
          </Card>

          <Card title="Variants" subtitle="Each variant is a bottle count customers can choose.">
            <div className="flex flex-col gap-3">
              {form.variants.map((v, i) => (
                <div key={v.id ?? `new-${i}`} className="grid gap-3 border border-black/10 p-3 sm:grid-cols-[1fr_110px_130px_100px_auto]">
                  <Field label="Label"><input className="inp" value={v.label} onChange={(e) => updateVariant(i, { label: e.target.value })} /></Field>
                  <Field label="Bottles"><input type="number" min={1} className="inp" value={v.bottleCount} onChange={(e) => updateVariant(i, { bottleCount: Number(e.target.value) })} /></Field>
                  <Field label="Bottles / box"><input type="number" min={1} className="inp" value={v.bottlesPerBox} onChange={(e) => updateVariant(i, { bottlesPerBox: Number(e.target.value) })} /></Field>
                  <Field label="Order"><input type="number" className="inp" value={v.sortOrder} onChange={(e) => updateVariant(i, { sortOrder: Number(e.target.value) })} /></Field>
                  <div className="flex items-end gap-3 pb-1">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.isActive} onChange={(e) => updateVariant(i, { isActive: e.target.checked })} /> Active</label>
                    <button type="button" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-red-700 hover:underline"
                      onClick={() => set("variants", form.variants.filter((_, x) => x !== i))}>Remove</button>
                  </div>
                </div>
              ))}
              <button type="button" className="self-start border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] hover:bg-black/5"
                onClick={() => set("variants", [...form.variants, { label: "", bottleCount: form.minBottles, bottlesPerBox: 5, sortOrder: form.variants.length, isActive: true }])}>
                Add variant
              </button>
            </div>
          </Card>

          <Card title="Marketing content">
            <Grid>
              <Field label="Short description" full><textarea className="inp min-h-16" value={form.shortDescription} onChange={(e) => set("shortDescription", e.target.value)} /></Field>
              <Field label="Description" full><textarea className="inp min-h-24" value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
              <Field label="Marketing text" full><textarea className="inp min-h-20" value={form.marketingText} onChange={(e) => set("marketingText", e.target.value)} /></Field>
              <Field label="SEO title" full><input className="inp" value={form.seo.title ?? ""} onChange={(e) => set("seo", { ...form.seo, title: e.target.value })} /></Field>
              <Field label="SEO description" full><textarea className="inp min-h-16" value={form.seo.description ?? ""} onChange={(e) => set("seo", { ...form.seo, description: e.target.value })} /></Field>
            </Grid>
          </Card>

          <Card title="Imagery" subtitle="Images come from the Media Library only.">
            <div className="grid gap-4 sm:grid-cols-3">
              {(["heroImage", "bannerImage", "thumbnailImage"] as const).map((key) => (
                <div key={key} className="flex flex-col gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-black/50">
                    {key === "heroImage" ? "Hero image" : key === "bannerImage" ? "Banner image" : "Thumbnail"}
                  </p>
                  <div className="flex h-32 items-center justify-center overflow-hidden border border-black/10 bg-black/[0.03]">
                    {form[key] ? <img src={form[key]} alt="" className="h-full w-full object-cover" /> : <span className="text-xs text-black/40">No image</span>}
                  </div>
                  <div className="flex gap-2">
                    <button type="button" className="border border-black/20 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-black/5" onClick={() => setPicker(key)}>Choose</button>
                    {form[key] && <button type="button" className="border border-black/20 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-black/5" onClick={() => set(key, "")}>Clear</button>}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Product overrides" subtitle={`${eligible.length} product(s) on the ${salesChannelLabel(form.salesChannel)} channel. Pin to show first, exclude to hide from this pack.`}>
            {eligible.length === 0 ? (
              <p className="text-sm text-black/60">
                No products carry this sales channel yet. Assign the channel in Products → edit → Sales channels.
              </p>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-black/10">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 border-b border-black/10 bg-white text-[10px] uppercase tracking-[0.18em] text-black/50">
                    <tr><th className="px-3 py-2">Product</th><th className="px-3 py-2">{form.bottleSizeMl} ML price</th><th className="px-3 py-2">Pinned</th><th className="px-3 py-2">Excluded</th><th className="px-3 py-2">Order</th></tr>
                  </thead>
                  <tbody>
                    {eligible.map((p) => {
                      const o = overrideFor(p.id);
                      return (
                        <tr key={p.id} className="border-b border-black/5 last:border-0">
                          <td className="px-3 py-2">
                            <div className="font-medium">{p.name}</div>
                            <div className="text-xs text-black/50">{p.family ?? "—"}</div>
                          </td>
                          <td className="px-3 py-2">{p.unitPrice > 0 ? formatPrice(p.unitPrice) : <span className="text-amber-700">No price</span>}</td>
                          <td className="px-3 py-2"><input type="checkbox" checked={o.isPinned} onChange={(e) => patchOverride(p.id, { isPinned: e.target.checked })} /></td>
                          <td className="px-3 py-2"><input type="checkbox" checked={o.isExcluded} onChange={(e) => patchOverride(p.id, { isExcluded: e.target.checked })} /></td>
                          <td className="px-3 py-2"><input type="number" className="w-20 border border-black/15 px-2 py-1" value={o.sortOrder} onChange={(e) => patchOverride(p.id, { sortOrder: Number(e.target.value) })} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <aside className="flex h-fit flex-col gap-3 rounded-sm border border-black/10 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-black/50">Configuration preview</p>
          <p className="text-lg font-semibold">{form.name || "Untitled pack"}</p>
          <p className="text-sm text-black/60">{form.shortDescription || "No short description yet."}</p>
          <dl className="mt-2 flex flex-col gap-2 text-sm">
            <Row label="Bottle size" value={`${form.bottleSizeMl} ML`} />
            <Row label="Variants" value={form.variants.filter((v) => v.isActive).map((v) => `${v.bottleCount} (${Math.ceil(v.bottleCount / Math.max(1, v.bottlesPerBox))} boxes)`).join(", ") || "—"} />
            <Row label="Selectable bottles" value={String(included.length)} />
            <Row label="Excluded" value={String(form.overrides.filter((o) => o.isExcluded).length)} />
            <Row label="Pinned" value={String(form.overrides.filter((o) => o.isPinned).length)} />
            <Row label="Bottle price range" value={included.length ? `${formatPrice(cheapest)} – ${formatPrice(dearest)}` : "—"} />
            {form.variants.filter((v) => v.isActive).map((v) => (
              <Row key={v.label} label={`Est. ${v.bottleCount}-bottle pack`} value={included.length ? `${formatPrice(cheapest * v.bottleCount)} – ${formatPrice(dearest * v.bottleCount)}` : "—"} />
            ))}
          </dl>
          <p className="mt-2 border-t border-black/10 pt-3 text-xs text-black/50">
            Packs are configuration only in this release — nothing here is shown to customers yet.
          </p>
        </aside>
      </div>

      <MediaPicker
        open={picker !== null}
        onClose={() => setPicker(null)}
        onSelect={(asset) => { if (picker) set(picker, asset.url); setPicker(null); }}
        title="Choose a pack image"
      />
    </div>
  );

  function updateVariant(index: number, patch: Partial<PackInput["variants"][number]>) {
    setForm((f) => ({ ...f, variants: f.variants.map((v, i) => (i === index ? { ...v, ...patch } : v)) }));
  }
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-sm border border-black/10 bg-white p-4">
      <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-black/70">{title}</h3>
      {subtitle && <p className="mt-1 text-xs text-black/50">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-black/50">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-black/50">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}